"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";

const FeatureFlagsContext = createContext();

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error(
      "useFeatureFlags must be used within a FeatureFlagsProvider"
    );
  }
  return context;
};

export const FeatureFlagsProvider = ({ children }) => {
  // The gated resource categories default to OFF, not ON. These flags drive
  // nav rendering, and until the real values arrive the server HTML would
  // otherwise link to /resources/{guides,case-studies,ebooks,research-papers}
  // — which middleware 307s to the homepage whenever a category is disabled.
  // Emitting a redirecting link on every page of the site burns crawl budget;
  // a briefly-missing link costs nothing, since the sitemap lists a category
  // as soon as it is enabled. Blogs stay ON because that section is the hub.
  const [featureFlags, setFeatureFlags] = useState({
    blogsEnabled: true,
    guidesEnabled: false,
    caseStudiesEnabled: false,
    ebooksEnabled: false,
    researchPapersEnabled: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Prevent duplicate fetches - global state
  const fetchingRef = useRef(false);
  const initializedRef = useRef(false);

  const fetchFeatureFlags = async () => {
    fetchingRef.current = true;

    try {
      setError(null);

      const response = await fetch("/api/featureFlags", {
        method: "GET",
        headers: {
          "Cache-Control": "no-cache",
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        // If rate limited, wait and don't spam logs. This must still record an
        // error: it returns without ever populating the flags, and consumers
        // need to tell "not loaded" apart from "loaded and disabled".
        if (response.status === 429) {
          console.warn("🚫 Rate limited, will retry later");
          setError("rate limited");
          return;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success || !result.data) {
        throw new Error("Invalid API response structure");
      }

      setFeatureFlags({
        blogsEnabled: result.data.blogsEnabled,
        guidesEnabled: result.data.guidesEnabled,
        caseStudiesEnabled: result.data.caseStudiesEnabled,
        ebooksEnabled: result.data.ebooksEnabled,
        researchPapersEnabled: result.data.researchPapersEnabled,
      });
    } catch (err) {
      console.warn(
        "Feature flags fetch failed, using safe defaults:",
        err.message
      );
      setError(err.message);
      // State is left untouched on error: `prev.x || true` used to force every
      // flag on, which re-introduced the redirecting resource links.
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  };

  // Initial fetch - only once globally
  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      fetchFeatureFlags();
    }
  }, []);

  // Refresh function for manual updates
  const refreshFeatureFlags = useCallback(() => {
    setLoading(true);
    return fetchFeatureFlags();
  }, []);

  const value = useMemo(
    () => ({
      featureFlags,
      loading,
      error,
      refreshFeatureFlags,
    }),
    [featureFlags, loading, error, refreshFeatureFlags]
  );

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};
